import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, EventEmitter, Injector, Input, OnInit, Output } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@services/notification.service';
import { ConfirmdialogComponent } from '@shared/components/confirmdialog/confirmdialog.component';
import { FileuploadComponent } from '@shared/components/fileupload/fileupload.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { TransferErrorPopupComponent } from '@shared/components/transfer-error-popup/transfer-error-popup.component';
import { GenericTableColumn } from '@shared/models/generictablecolumn';
import { CuttingService, DivisionType, DivisionTypeTranslation, IColor, IConfig, IMachine, IMachineDetails, IResponseBasket, IResponseConfig } from '@shared/services/cutting.service';
import { ConfigTransferService } from '@shared/services/export-import-services/config';
import { FeatureFlagsService } from '@shared/services/featureflags/featureflags.service';
import { ImportValidatorService } from '@shared/services/filevalidators/Importvalidator.service';
import { IAccount } from '@shared/services/user.service';
import { catchError, finalize, map, Subject, takeUntil, throwError } from 'rxjs';
import { CreateUpdateColorDialogComponent } from './create-update-color/create-update-color';
import { CreateUpdateMachineDialogComponent } from './create-update-machine/create-update-machine';

@Component({
	selector: 'app-configs',
	templateUrl: './configs.component.html',
	styleUrls: ['./configs.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ConfigsComponent implements OnInit {
	private self: string;
	private configData: IResponseConfig;
	public isLoading: boolean = false;
	public showSearch: boolean = false;
	public showPaginator = false;
	public showGenerateRodsForWaste: boolean = false;
	public configSource = new MatTableDataSource<IConfig>();
	public machinesSource = new MatTableDataSource<IMachineDetails>();
	public colorsSource = new MatTableDataSource<IColor>();
	public configsFilter: string = '';
	public unsubscribe: Subject<void> = new Subject<void>();
	public loadingSubject: Subject<boolean> = new Subject<boolean>();
	public baskets: Map<string, IResponseBasket> = new Map<string, IResponseBasket>();

	public extraRodsFormControl: FormControl = new FormControl('', [Validators.required]);

	/**
	 * We keep track of the validity of the extra rods form, so we can disable the check boxes if that's the case. The reason is that it blocks checking the boxes while the percentage might be invalid. The "element" Object would then have an invalid value and the update() would fail, since we generate the update config
	 * from the "element"
	 */
	public extraRodsFormValid = true;

	@Output() menuEmitter = new EventEmitter<object>();
	@Input() public rowActions: any[];

	public configsTableColumns: string[] = ['allowFrameAndSashInSameBasket', 'allowMixedCrossSectionInSameBasket', 'extraRodsPercentage'];

	public machinesTableColumns: GenericTableColumn[] = [
		{ name: 'masterdata.cutting.name', dataKey: 'name' },
		{
			name: 'masterdata.cutting.divisionType.name',
			dataKey: 'divisionTypeTranslation',
			isTranslatable: true,
		},
	];

	public colorsTableColumns: GenericTableColumn[] = [{ name: 'masterdata.cutting.colors.combination', dataKey: 'colors' }];

	public regRowActions: any[] = [
		{
			title: 'masterdata.cutting.actions.edit',
			handler: 'editMasterdataRow',
		},
		{
			title: 'masterdata.cutting.actions.delete',
			handler: 'deleteMasterdataRow',
		},
	];

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_add',
			text: () => 'masterdata.cutting.newbtn.machines',
			click: () => this.openCreateUpdateMachine(),
		},
		{
			icon: () => 'acies_add',
			text: () => 'masterdata.cutting.newbtn.colors',
			click: () => this.openCreateUpdateColor(),
		},
	];

	menuItems: SubMenuItem[] = [
		{
			icon: () => 'acies_download',
			text: () => 'masterdata.cutting.configImportExport.import.button',
			click: () => this.onImport(),
			permissions: () => ['cutting:ConfigPut', 'cutting:ConfigList'],
		},
		{
			icon: () => 'acies_upload',
			text: () => 'masterdata.cutting.configImportExport.export.button',
			click: () => this.onExport(),
			permissions: () => ['cutting:ConfigList'],
		},
		{
			icon: () => 'acies_upload',
			text: () => 'masterdata.cutting.configImportExport.import.template.button',
			click: () => this.onDownloadTemplate(),
			permissions: () => ['cutting:ConfigList'],
		},
	];

	public currentConfig: IConfig | null = null;

	constructor(
		public dialog: MatDialog,
		private translator: TranslateService,
		private cuttingService: CuttingService,
		private notificationService: NotificationService,
		private featureFlagsService: FeatureFlagsService,
		private configTransferService: ConfigTransferService,
		private injector: Injector,
	) {}

	ngOnDestroy() {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	ngOnInit(): void {
		this.isLoading = true;
		this.HandleFlagShowCreateWasteProfile();
		this.loadBaskets();
	}

	private loadBaskets(account?: IAccount): void {
		this.cuttingService
			.getBaskets(account)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((baskets) => {
				this.baskets = new Map(baskets.items.map((basket) => [basket.self, basket]));
				this.loadData();
			});
	}

	public saveConfiguration(): void {
		if (!this.currentConfig) {
			return;
		}
		this.isLoading = true;
		this.cuttingService
			.updateConfig(this.currentConfig)
			.pipe(catchError(this.handleError), takeUntil(this.unsubscribe))
			.subscribe({
				next: () => {
					this.notificationService.notify(['masterdata.cutting.delete.configs.notification.header'], ['masterdata.cutting.delete.configs.notification.content', { name: 'machineData.name' }], 'success');
					this.currentConfig = null;
					this.loadData();
				},
				error: () => {
					this.isLoading = false;
				},
			});
	}

	public resetConfiguration(): void {
		this.currentConfig = null;
		this.loadData();
	}

	public onExport(): void {
		if (!this.configData) {
			return;
		}

		this.configTransferService.exportData([this.configData]).subscribe({
			next: () => {
				this.notificationService.notify(['masterdata.cutting.configImportExport.export.notification.success.header'], ['masterdata.cutting.configImportExport.export.notification.success.content'], 'success');
			},
			error: (error: Error) => {
				console.error('Failed to export configuration', error);
				this.notificationService.notify(['masterdata.cutting.configImportExport.export.notification.error.header'], ['masterdata.cutting.configImportExport.export.notification.error.content'], 'error');
				this.dialog.open(TransferErrorPopupComponent, {
					width: '450px',
					data: { message: error.message },
				});
			},
		});
	}

	public onDownloadTemplate(): void {
		this.configTransferService.downloadTemplate();
	}

	public onImport(): void {
		const dialogRef = this.dialog.open(FileuploadComponent, {
			width: '450px',
			data: {
				headingKey: 'masterdata.cutting.configImportExport.import.title',
				subHeadingKey: 'masterdata.cutting.configImportExport.import.subtitle',
				confirmButtonKey: 'masterdata.cutting.configImportExport.import.button',
				notificationHeader: 'masterdata.cutting.configImportExport.import.notification.success.header',
				notificationContent: 'masterdata.cutting.configImportExport.import.notification.success.content',
				notificationErrorHeader: 'masterdata.cutting.configImportExport.import.notification.error.header',
				notificationErrorContent: 'masterdata.cutting.configImportExport.import.notification.error.content',
				notificationClientErrorHeader: 'masterdata.cutting.configImportExport.import.notification.clientError.header',
				notificationClientErrorContent: 'masterdata.cutting.configImportExport.import.notification.clientError.content',
				detailedClientErrorMessage: 'fileuploader.detailed-client-error-message',
				allowedFileTypes: ['.xlsx'],
				validatorService: this.injector.get(ImportValidatorService),
				data: this.configTransferService,
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(() => {
				this.loadData();
			});
	}

	public updateConfig(element: any): void {
		this.extraRodsFormValid = this.extraRodsFormControl.valid;
		if (this.extraRodsFormValid) {
			this.currentConfig = this.getConfig(element);
		} else {
			this.currentConfig = null;
		}
	}

	private getConfig(element: any): IConfig {
		return {
			self: element.self,
			prohibitedColorPairing: element.prohibitedColorPairing,
			allowFrameAndSashInSameBasket: element.allowFrameAndSashInSameBasket,
			allowMixedCrossSectionInSameBasket: element.allowMixedCrossSectionInSameBasket,
			generateRodsForWaste: element.generateRodsForWaste,
			extraRodsPercentage: element.extraRodsPercentage,
			machines: this.machinesSource.data.map((machine) => {
				return {
					self: machine.self,
					divisionType: machine.divisionType,
				};
			}),
		};
	}

	private HandleFlagShowCreateWasteProfile() {
		if (this.featureFlagsService.isFlagEnabled('ShowCreateWasteProfile')) {
			this.showGenerateRodsForWaste = true;
			this.configsTableColumns.splice(2, 0, 'generateRodsForWaste');
		} else {
			// reset if shown on another account before
			this.showGenerateRodsForWaste = false;
			this.configsTableColumns = this.configsTableColumns.filter((column) => column !== 'generateRodsForWaste');
		}
	}

	loadData(): void {
		this.isLoading = true;

		this.cuttingService
			.getConfigs()
			.pipe(
				takeUntil(this.unsubscribe),
				map((data) => {
					this.self = data.items[0].self;
					this.configData = data.items[0];

					const configs = data.items.map((config) => ({
						self: config.self,
						prohibitedColorPairing: config.prohibitedColorPairing,
						allowFrameAndSashInSameBasket: config.allowFrameAndSashInSameBasket,
						allowMixedCrossSectionInSameBasket: config.allowMixedCrossSectionInSameBasket,
						generateRodsForWaste: config.generateRodsForWaste,
						extraRodsPercentage: config.extraRodsPercentage ? config.extraRodsPercentage : 0,
						machines: null,
					}));

					const machines = data.items.flatMap((config) =>
						config.machines.map((machine) => {
							const name = machine.self.split('/').pop();
							return {
								self: machine.self,
								name,
								divisionType: machine.divisionType,
							};
						}),
					);

					const colors = data.items.flatMap((config) =>
						config.prohibitedColorPairing.map((colorArr) => ({
							colors: colorArr.map((color, index) => (index === 0 ? color.charAt(0).toUpperCase() + color.slice(1).toLowerCase() : color)).join(', '),
						})),
					);

					return { configs, machines, colors };
				}),
				finalize(() => {
					this.isLoading = false;
				}),
			)
			.subscribe(({ configs, machines, colors }) => {
				this.configSource.data = configs;
				this.machinesSource.data = machines.map((m) => this.translate(m));
				this.colorsSource.data = colors;
			});
	}

	translate(m: IMachine): IMachineDetails {
		const divisionTypeTranslationMap: Record<DivisionType, DivisionTypeTranslation> = {
			BetweenEveryOptimizationGroup: 'masterdata.cutting.divisionType.betweenEveryOptimizationGroup',
			BetweenEveryMaterial: 'masterdata.cutting.divisionType.betweenEveryMaterial',
			BetweenEveryRule: 'masterdata.cutting.divisionType.betweenEveryRule',
			Never: 'masterdata.cutting.divisionType.never',
		};

		return {
			divisionType: m.divisionType,
			name: m.name,
			self: m.self,
			divisionTypeTranslation: divisionTypeTranslationMap[m.divisionType],
		};
	}

	handleRowMenuActionMachines(data: object): void {
		switch (data['action'].handler) {
			case 'editMasterdataRow':
				this.openCreateUpdateMachine(data['data']);
				break;
			case 'deleteMasterdataRow':
				this.onDeleteMachine(data['data']);
				break;
			default:
				break;
		}
	}

	handleRowMenuActionColors(data: object): void {
		switch (data['action'].handler) {
			case 'editMasterdataRow':
				this.openCreateUpdateColor(data['data']);
				break;
			case 'deleteMasterdataRow':
				this.onDeleteColor(data['data']);
				break;
			default:
				break;
		}
	}

	handleReloadData(_: object): void {
		// Empty function to enable client side only pagination
		// this.loadData(data['pageSize'] * data['pageNumber']);
	}

	openCreateUpdateMachine(machineData: object = null) {
		const otherMachines = this.machinesSource.data.filter((machine) => machine !== machineData);

		const dialogRef = this.dialog.open(CreateUpdateMachineDialogComponent, {
			disableClose: true,
			width: '650px',
			data: {
				self: this.self,
				configData: this.configData,
				editMachine: machineData,
				otherMachines,
			},
		});
		dialogRef.componentInstance.saved.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
			this.loadData();
		});
	}

	openCreateUpdateColor(colorData: object = null) {
		const otherColors = this.colorsSource.data.filter((color) => color !== colorData);

		const dialogRef = this.dialog.open(CreateUpdateColorDialogComponent, {
			disableClose: true,
			width: '650px',
			data: {
				self: this.self,
				configData: this.configData,
				editColor: colorData,
				otherColors,
			},
		});
		dialogRef.componentInstance.saved.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
			this.loadData();
		});
	}

	private handleError(error: HttpErrorResponse) {
		if (error.status === 0) {
			// A client-side or network error occurred. Handle it accordingly.
			console.error('An error occurred:', error.error);
		} else {
			// The backend returned an unsuccessful response code.
			// The response body may contain clues as to what went wrong.
			console.error(`Backend returned code ${error.status}, body was: `, error.error);
		}
		// Return an observable with a user-facing error message.
		return throwError(() => new Error('Something bad happened; please try again later.'));
	}

	onDeleteMachine(machineData: IMachineDetails) {
		const config: IConfig = {
			self: this.self,
			prohibitedColorPairing: this.configData.prohibitedColorPairing,
			allowFrameAndSashInSameBasket: this.configData.allowFrameAndSashInSameBasket,
			allowMixedCrossSectionInSameBasket: this.configData.allowMixedCrossSectionInSameBasket,
			generateRodsForWaste: this.configData.generateRodsForWaste,
			extraRodsPercentage: this.configData.extraRodsPercentage,
			machines: this.machinesSource.data
				.filter((machine) => machine !== machineData)
				.map((machine) => {
					return {
						self: machine.self,
						divisionType: machine.divisionType,
					};
				}),
		};

		const dialogRef = this.dialog.open(ConfirmdialogComponent, {
			width: '450px',
			data: {
				okReturnData: true,
				cancelReturnData: false,
				heading: this.translator.instant('masterdata.cutting.delete.machines.heading'),
				content: this.translator.instant('masterdata.cutting.delete.machines.content', {
					name: machineData.name,
				}),
				okButtonText: this.translator.instant('general.ok'),
				cancelButtonText: this.translator.instant('general.cancel'),
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((result) => {
				if (result) {
					this.cuttingService
						.updateConfig(config)
						.pipe(catchError(this.handleError))
						.subscribe(() => {
							this.notificationService.notify(['masterdata.cutting.delete.machines.notification.header'], ['masterdata.cutting.delete.machines.notification.content', { name: machineData.name }], 'success');
							this.loadData();
						});
				}
			});
	}

	onDeleteColor(colorData: IColor) {
		const config: IConfig = {
			self: this.self,
			prohibitedColorPairing: this.colorsSource.data.filter((color) => color !== colorData).map((color) => [color.colors]),
			allowFrameAndSashInSameBasket: this.configData.allowFrameAndSashInSameBasket,
			allowMixedCrossSectionInSameBasket: this.configData.allowMixedCrossSectionInSameBasket,
			generateRodsForWaste: this.configData.generateRodsForWaste,
			extraRodsPercentage: this.configData.extraRodsPercentage,
			machines: this.machinesSource.data.map((machine) => {
				return {
					self: machine.self,
					divisionType: machine.divisionType,
				};
			}),
		};

		const dialogRef = this.dialog.open(ConfirmdialogComponent, {
			width: '450px',
			data: {
				okReturnData: true,
				cancelReturnData: false,
				heading: this.translator.instant('masterdata.cutting.delete.colors.heading'),
				content: this.translator.instant('masterdata.cutting.delete.colors.content', {
					name: colorData,
				}),
				okButtonText: this.translator.instant('general.ok'),
				cancelButtonText: this.translator.instant('general.cancel'),
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((result) => {
				if (result) {
					this.cuttingService
						.updateConfig(config)
						.pipe(catchError(this.handleError))
						.subscribe(() => {
							this.notificationService.notify(['masterdata.cutting.delete.colors.notification.header'], ['masterdata.cutting.delete.colors.notification.content', { name: colorData }], 'success');
							this.loadData();
						});
				}
			});
	}
}
